import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";

export default function Cart() {
  const { cart, updateCart, removeFromCart } = useShop();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const delivery = cart.total >= 999 || cart.total === 0 ? 0 : 49;

  if (loading) return <div className="py-32 text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gold border-t-transparent" /></div>;

  if (!user?.user_id) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-extrabold text-midnight">Sign in to view your cart</h1>
        <Link to="/login" className="mt-6 inline-block rounded-full bg-midnight px-8 py-3 font-bold text-white">Login</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-10">
      <h1 className="text-3xl font-extrabold text-midnight mb-8">Your Cart <span className="text-muted-foreground text-lg">({cart.count})</span></h1>
      {cart.items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border py-24 text-center">
          <p className="text-lg font-bold text-midnight">Your cart is empty</p>
          <Link to="/shop" className="mt-4 inline-block rounded-full bg-midnight px-8 py-3 font-bold text-white">Start Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.items.map(({ product, quantity, line_total }) => (
              <div key={product.id} className="flex gap-4 rounded-2xl border border-border p-4">
                <Link to={`/product/${product.slug}`}><img src={product.images[0]} alt={product.name} className="h-24 w-24 rounded-xl object-cover" /></Link>
                <div className="flex-1">
                  <p className="text-[11px] font-bold uppercase text-muted-foreground">{product.brand}</p>
                  <Link to={`/product/${product.slug}`} className="text-sm font-bold text-midnight line-clamp-1">{product.name}</Link>
                  {product.mart === "wood-mart" && <p className="text-xs text-mart-wood">Furniture • Delivery & installation applies</p>}
                  <div className="mt-2 flex items-center gap-4">
                    <div className="flex items-center rounded-full border border-border">
                      <button onClick={() => updateCart(product.id, quantity - 1)} className="grid h-8 w-8 place-items-center" data-testid={`qty-dec-${product.slug}`}><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-8 text-center text-sm font-bold">{quantity}</span>
                      <button onClick={() => updateCart(product.id, quantity + 1)} className="grid h-8 w-8 place-items-center" data-testid={`qty-inc-${product.slug}`}><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button onClick={() => removeFromCart(product.id)} className="text-red-600" data-testid={`remove-${product.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="text-right"><p className="font-extrabold text-midnight">{inr(line_total)}</p></div>
              </div>
            ))}
          </div>

          <div className="h-fit rounded-3xl border border-border p-6">
            <h2 className="text-lg font-extrabold text-midnight mb-4">Order Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{inr(cart.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="font-semibold">{delivery === 0 ? "Free" : inr(delivery)}</span></div>
              {cart.total < 999 && cart.total > 0 && <p className="text-xs text-gold-dark">Add {inr(999 - cart.total)} more for free delivery</p>}
              <div className="border-t border-border pt-3 flex justify-between text-base"><span className="font-bold">Total</span><span className="font-extrabold" data-testid="cart-total">{inr(cart.total + delivery)}</span></div>
            </div>
            <button onClick={() => navigate("/checkout")} data-testid="checkout-btn" className="mt-5 w-full rounded-full bg-gold py-3.5 font-bold text-midnight hover:bg-gold-soft transition-colors">Proceed to Checkout</button>
          </div>
        </div>
      )}
    </div>
  );
}
